import React, { useCallback, useEffect, useRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import CustomCodeSection from 'src/js/modules/common/components/custom-code-section';
import customCodeTriggerIds from 'src/js/modules/common/constants/custom-code-trigger-ids';
import IntegrationsSection from './integrations-section';
import GeneralSection from './general-section';
import MultilingualTabContainer from '../containers/multilingual-tab-container';
import BackupsSection from './backups-section';
import constants from '../constants';
import HostingSection from './hosting-section';
import DomainSection from './domain-section';
import BusinessInfoSection from './business-info-section';
import dialogTypes from '../../dialogs/constants/dialog-types';
import { useWebsiteSettingsContext } from '../context/website-settings-context';

const { webSettingsConfig, tabIdentifiers, settingTypes } = constants;
const {
  Tabs,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Heading,
} = designSystem;

const defaultScrollIntoView = () => Promise.resolve();
const defaultStyles = {};

const WebsiteSettings = React.forwardRef((_, ref) => {
  const {
    scrollElementIntoView = defaultScrollIntoView,
    settingToScroll = '',
    setActiveTabId,
    captions,
    styles = defaultStyles,
    className = '',
    mobile,
    onSubmit,
    onCancel,
    width,
    height,
    onKeyDown,
    showPromptForce,
    activeTabId,
    onTabChange,
    formValues,
    formErrors,
    isBusinessInfoEnabled,
    isSyndicatedHosting,
    isDomainTabEnabled,
    showWebsiteSettingsDialog,
    control,
  } = useWebsiteSettingsContext();

  const siteNameRef = useRef(null);
  const addressesRef = useRef(null);
  const phonesRef = useRef(null);
  const emailsRef = useRef(null);
  const businessHoursRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    let elementToScroll = null;

    if (settingToScroll === settingTypes.ADDRESSES) {
      elementToScroll = addressesRef.current;
    }

    if (settingToScroll === settingTypes.PHONES) {
      elementToScroll = phonesRef.current;
    }

    if (settingToScroll === settingTypes.EMAILS) {
      elementToScroll = emailsRef.current;
    }

    if (settingToScroll === settingTypes.BUSINESS_HOURS) {
      elementToScroll = businessHoursRef.current;
    }

    if (elementToScroll) {
      scrollElementIntoView(scrollContainer, elementToScroll);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const onFormSubmit = useCallback(
    ({ errors, onSubmit: handleSubmit }) => {
      const fieldsErrorKeys = Object.keys(errors);
      const fieldsErrorValues = Object.values(errors);
      const emptyMessage = '';

      if (fieldsErrorKeys.length && !fieldsErrorValues.includes(emptyMessage)) {
        const firstErrorField = webSettingsConfig.find((field) =>
          fieldsErrorKeys.includes(field.name)
        );

        setActiveTabId(firstErrorField.tabId).then(() => {
          const scrollContainer = scrollContainerRef.current;
          let firstErrorFieldElement = null;

          if (firstErrorField.name === settingTypes.SITE_NAME) {
            firstErrorFieldElement = siteNameRef.current;
          }

          if (firstErrorFieldElement) {
            scrollElementIntoView(scrollContainer, firstErrorFieldElement);
          }
        });

        return;
      }

      handleSubmit();
    },
    [scrollElementIntoView, setActiveTabId]
  );

  const websiteSettingsClasses = classNames('ws-website-settings', className);

  const modalClasses = classNames(
    'ws-website-settings__modal',
    mobile && 'ws-website-settings__modal--mobile'
  );

  const submitForm = useCallback(() => {
    onFormSubmit({ onSubmit, errors: formErrors });
  }, [onFormSubmit, onSubmit, formErrors]);

  useImperativeHandle(ref, () => ({
    submit() {
      submitForm();
    },
  }));

  return (
    <div className={websiteSettingsClasses} style={styles}>
      <Modal
        promptCaptions={captions.prompt}
        handleSubmit={submitForm}
        handleCancel={onCancel}
        data={formValues}
        className={modalClasses}
        width={width}
        height={height}
        centered
        overlay="visible"
        draggable={false}
        resizable={false}
        fullscreen={mobile}
        onKeyDown={onKeyDown}
        showPromptForce={showPromptForce}
      >
        <PanelGroup height="100%">
          <Panel
            align="middle"
            corners="squared"
            theme="gray-100"
            className="ws-panel--shrink ws-website-settings__header"
          >
            <Heading type="heading-6">{captions.websiteSettings}</Heading>
          </Panel>
          <div className="ws-website-settings__tabs">
            <Tabs
              activeTabId={activeTabId}
              onTabChange={onTabChange}
              scrollContainerRef={scrollContainerRef}
            >
              <TabsList>
                <Tab tabId={tabIdentifiers.GENERAL} label={captions.tabs.general} />
                {isBusinessInfoEnabled && (
                  <Tab tabId={tabIdentifiers.BUSINESS_INFO} label={captions.tabs.businessInfo} />
                )}
                <Tab tabId={tabIdentifiers.MULTILINGUAL} label={captions.tabs.multilingual} />
                {isDomainTabEnabled && (
                  <Tab tabId={tabIdentifiers.DOMAIN} label={captions.tabs.domain} />
                )}
                <Tab tabId={tabIdentifiers.INTEGRATIONS} label={captions.tabs.integrations} />
                <Tab tabId={tabIdentifiers.CUSTOM_CODE} label={captions.tabs.customCode} />
                {isSyndicatedHosting && (
                  <Tab tabId={tabIdentifiers.HOSTING} label={captions.tabs.hosting} />
                )}
                <Tab tabId={tabIdentifiers.BACKUPS} label={captions.tabs.siteBackups} />
              </TabsList>
              <TabsContentList>
                <TabContent tabId={tabIdentifiers.GENERAL}>
                  <GeneralSection siteNameRef={siteNameRef} />
                </TabContent>
                {isBusinessInfoEnabled && (
                  <TabContent tabId={tabIdentifiers.BUSINESS_INFO}>
                    <BusinessInfoSection
                      phonesRef={phonesRef}
                      addressesRef={addressesRef}
                      emailsRef={emailsRef}
                      businessHoursRef={businessHoursRef}
                    />
                  </TabContent>
                )}
                <TabContent tabId={tabIdentifiers.MULTILINGUAL}>
                  <MultilingualTabContainer />
                </TabContent>
                {isDomainTabEnabled && (
                  <TabContent tabId={tabIdentifiers.DOMAIN}>
                    <DomainSection />
                  </TabContent>
                )}
                <TabContent tabId={tabIdentifiers.INTEGRATIONS}>
                  <IntegrationsSection />
                </TabContent>
                <TabContent tabId={tabIdentifiers.CUSTOM_CODE}>
                  <CustomCodeSection
                    triggerId={customCodeTriggerIds.WEBSITE_SETTINGS}
                    dialogType={dialogTypes.WEBSITE_SETTINGS}
                    control={control}
                    captions={captions.customCode}
                    showSettingsDialog={(formData) =>
                      showWebsiteSettingsDialog({
                        activeTabId: tabIdentifiers.CUSTOM_CODE,
                        formData,
                      })
                    }
                  />
                </TabContent>
                {isSyndicatedHosting && (
                  <TabContent tabId={tabIdentifiers.HOSTING}>
                    <HostingSection />
                  </TabContent>
                )}
                <TabContent tabId={tabIdentifiers.BACKUPS}>
                  <BackupsSection />
                </TabContent>
              </TabsContentList>
            </Tabs>
          </div>
          <Panel className="ws-website-settings__footer" align="middle" corners="squared">
            <DialogButtonGroup rounded>
              <DialogButton onClick={submitForm}>{captions.apply}</DialogButton>

              <DialogButtonGroupSeparator />

              <DialogButton onClick={onCancel}>{captions.cancel}</DialogButton>
            </DialogButtonGroup>
          </Panel>
        </PanelGroup>
      </Modal>
    </div>
  );
});

WebsiteSettings.displayName = 'WebsiteSettings';

export default WebsiteSettings;
